import api from './api';

/**
 * Customer grievances (board #393, phase 1).
 *
 * A grievance is a support ticket with `is_grievance=true`, so this reads and
 * writes `/communications/tickets/`; the server limits a customer to their own
 * tickets and their own policies. Escalation is its own action
 * (`POST .../escalate/`) -- the level is read-only everywhere else.
 */

export interface EscalationLevel {
  level: 1 | 2 | 3;
  label: string;
  name: string;
  email: string;
  phone: string;
  url: string;
  note?: string;
  is_current: boolean;
  is_reached: boolean;
}

export interface GrievanceHistoryRow {
  activity_type: 'created' | 'status_changed' | 'escalated';
  description: string;
  old_value: string;
  new_value: string;
  created_at: string;
}

export interface Grievance {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  status: string;
  category_name: string | null;
  policy: string | null;
  policy_number: string | null;
  insurer: string | null;
  insurer_name: string | null;
  escalation_level: number | null;
  escalation_level_display: string | null;
  sla_due_at: string | null;
  is_tat_breached: boolean;
  created_at: string;
  escalation_matrix?: EscalationLevel[] | null;
  status_history?: GrievanceHistoryRow[] | null;
}

export interface GrievanceCreatePayload {
  subject: string;
  description: string;
  support_category: string;
  policy: string;
}

export interface GrievanceCategory {
  id: string;
  name: string;
  resolution_tat_days: number | null;
}

export interface GrievancePolicyOption {
  id: string;
  policy_number: string;
  insurer_name?: string;
}

type Paged<T> = { results: T[] } | T[];
const rows = <T>(data: Paged<T>): T[] => (Array.isArray(data) ? data : data.results);

export const grievancesApi = {
  async list(): Promise<Grievance[]> {
    const { data } = await api.get<Paged<Grievance>>('/communications/tickets/', {
      params: { is_grievance: 'true', page_size: 100 },
    });
    return rows(data);
  },

  async get(id: string): Promise<Grievance> {
    const { data } = await api.get<Grievance>(`/communications/tickets/${id}/`);
    return data;
  },

  async create(payload: GrievanceCreatePayload): Promise<Grievance> {
    const { data } = await api.post<Grievance>('/communications/tickets/', {
      ...payload,
      is_grievance: true,
    });
    return data;
  },

  async escalate(id: string, reason: string): Promise<Grievance> {
    const { data } = await api.post<Grievance>(`/communications/tickets/${id}/escalate/`, {
      reason,
    });
    return data;
  },

  async categories(): Promise<GrievanceCategory[]> {
    const { data } = await api.get<Paged<GrievanceCategory>>('/communications/ticket-categories/', {
      params: { page_size: 200 },
    });
    return rows(data);
  },

  async policies(): Promise<GrievancePolicyOption[]> {
    const { data } = await api.get<Paged<GrievancePolicyOption>>('/policies/', {
      params: { page_size: 100 },
    });
    return rows(data);
  },
};
